<template>
  <div class="container">
    <div class="hide-on-print" style="width: 500px">
      <title-block title="Logbook Int." class="accountstitle" :callback="GetOrRefreshData"></title-block>
      <b-field label="Project">
        <b-autocomplete
          v-model="projectFilter"
          placeholder="select or filter a project"
          :keep-first="false"
          :open-on-focus="true"
          :data="filteredDataObj"
          :custom-formatter="displayName"
          @select="option => (selectedProject = option)"
          :clearable="true"
          @input="projectChangedHandler"
        >
        </b-autocomplete>
      </b-field>

      <b-field label="Building">
        <b-select placeholder="Select a building" @input="buildingChangedHandler" expanded>
          <option v-for="building in buildings" :value="building" :key="building.buildingId">
            {{ building.name }}
          </option>
        </b-select>
      </b-field>

      <b-field label="Floor">
        <b-select placeholder="Select a floor" @input="storyChangedHandler" expanded>
          <option v-for="floor in floors" :value="floor" :key="floor.storyId">{{ floor.name }}</option>
        </b-select>
      </b-field>

      <b-field label="Zone">
        <b-select placeholder="Select zone" @input="zoneChangedHandler" expanded>
          <option v-for="zone in zones" :value="zone" :key="zone.zoneId">{{ zone.name }}</option>
        </b-select>
      </b-field>
      <br />

      <div v-if="hasOpenings">
        <b-field label="Filter by Type">
          <b-select v-model="selectedTypeFilter" expanded>
            <option value="">— All types —</option>
            <option v-for="t in availableTypes" :value="t" :key="t">{{ translateType(t) }}</option>
          </b-select>
        </b-field>

        <b-field label="Filter by System">
          <b-select v-model="selectedSystemFilter" expanded>
            <option value="">— All systems —</option>
            <option v-for="s in availableSystems" :value="s" :key="s">{{ translateSystem(s) }}</option>
          </b-select>
        </b-field>
      </div>
      <br />

      <h2 v-if="!hasOpenings && selectedZone">No penetrations found.</h2>
    </div>

    <b-loading :is-full-page="true" :active.sync="isLoading" :can-cancel="false"></b-loading>

    <div id="forprint" v-if="selectedZone != null">
      <div class="filterbox hide-on-print">
        <h2>Filter by drawing id, separated by spaces, e.g.: 9 14 27</h2>
        <textarea v-model="filterids" rows="5" cols="30" />
      </div>

      <div class="only-show-on-print">
        <h4 class="title is-4" style="margin: 2px">Logbook:</h4>
        <table>
          <tr>
            <td>Project</td>
            <td>{{ selectedProject ? selectedProject.name : '' }}</td>
          </tr>
          <tr>
            <td>Building</td>
            <td>{{ selectedBuilding ? selectedBuilding.name : '' }}</td>
          </tr>
          <tr>
            <td>Floor</td>
            <td>{{ selectedFloor ? selectedFloor.name : '' }}</td>
          </tr>
          <tr>
            <td>Zone</td>
            <td>{{ selectedZone ? selectedZone.name : '' }}</td>
          </tr>
        </table>
      </div>

      <div class="printblock bordered" v-for="opening in filteredOpenings" :key="opening.openingId">
        <div class="columns">
          <div class="column">
            <table class="printblock-table">
              <tr>
                <td>QR code</td>
                <td>{{ opening.tagId }}</td>
                <td>User</td>
                <td-user :username="opening.lastUser" />
              </tr>
              <tr>
                <td>Id on drawing</td>
                <td>{{ opening.idOnDrawing }}</td>
                <td>Date</td>
                <td>{{ opening.lastDate | prettyDate }}</td>
              </tr>
              <tr>
                <td>Room</td>
                <td>{{ opening.room }}</td>
                <td>Status</td>
                <td v-if="opening.photos !== null && opening.photos.length > 0">{{ opening.lastStatus | status }}</td>
              </tr>
              <tr>
                <td>Fire rating</td>
                <td>{{ opening.fireRating }}</td>
                <td>Comment</td>
                <td v-if="opening.photos !== null && opening.photos.length > 0">{{ opening.lastComment }}</td>
              </tr>
              <tr>
                <td>Wall/floor assembly</td>
                <td>{{ opening.wallFloorAssembly }}</td>
              </tr>
              <tr>
                <td>Remark</td>
                <td colspan="3">{{ opening.comment }}</td>
              </tr>
            </table>
          </div>

          <div class="column is-one-third logbook-values">
            <div v-if="opening.lastphoto">
              <img :src="opening.lastphoto.uri" class="photo" />
            </div>
          </div>
        </div>

        <div class="penetrant">
          <h3>Penetrations:</h3>
          <div v-for="penetrant in filteredPenetrants(opening)" :key="penetrant.penetrantId">
            <table class="printblock-table">
              <tr>
                <td width="8%">Type</td>
                <td width="40%">{{ translateType(penetrant.type) }}</td>
                <td width="8%">Size</td>
                <td width="8%">{{ penetrant.sizeCategory }}</td>
                <td width="8%">Count</td>
                <td width="6%">{{ penetrant.count }}</td>
                <td width="6%">Insulation</td>
                <td width="16%">{{ penetrant.isolation }}</td>
              </tr>
              <tr>
                <td>System</td>
                <td>{{ translateSystem(penetrant.system) }}</td>
                <td>Length</td>
                <td>{{ penetrant.length }}</td>
                <td>Complexity</td>
                <td>{{ penetrant.complexityFactor }}</td>
              </tr>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import LogbookItemLabel from './LogbookItemLabel'
import ProjectService from '../../services/projectService'
import OpeningService from '../../services/openingService'
import TitleBlock from '@/components/General/TitleBlock'
import _ from 'lodash'
import TdUser from '../General/TdUser.vue'

const TYPE_TRANSLATIONS = {
  'LSR': 'Penetration round empty',
  'LSV': 'Penetration square empty',
  'LKV-S': 'Duct rectangular no-damper no-insul smoke-seal',
  'BKV-IN': 'Duct rectangular damper at opening',
  'BKV-OUT': 'Duct rectangular damper placed outside opening',
  'LKV-BB': 'Duct rectangular cladding full-length',
  'LKR-S': 'Duct round no-damper no-insul smoke-seal',
  'BKR-IN': 'Duct round damper placed in opening',
  'BKR-OUT': 'Duct round damper placed outside opening',
  'LKR-BB': 'Duct round cladding full-length',
  'LK-zaag': 'Duct cutting butterfly-damper',
  'LKR-pas': 'Duct round transition-piece',
  'LK-XX': 'Duct enlargement/reduction damper',
  'KG/KL': 'Cable Tray/cable ladder',
  'KP/K1': 'Cable single',
  'KB': 'Cable bundle',
  'KM': 'Cable conduit <100mm',
  'WCD': 'Outlet elec. single',
  'WCD-2': 'Outlet elec. multi',
  'PP (1z)': 'Pipe combust no-insul 1-side',
  'PP (2z)': 'Pipe combust no-insul 2-side',
  'PP+': 'Pipe combust insul',
  'PP-grp': 'Pipe combust bundle',
  'PP scheef': 'Pipe combust angled',
  'PP-UB': 'Pipe combust cover-plate',
  'PP opstort': 'Pipe combust concrete footing block',
  'MB0': 'Pipe metal no-insul',
  'MBA': 'Pipe metal Insul - armaflex',
  'MBS': 'Pipe metal mineralwool-insul',
  'MBG': 'Pipe metal glasswool-insul',
  'MBX': 'Pipe metal rigid-combust-insul',
  'LV-V': 'Joint linear vertical (per m)',
  'DIL-V': 'Joint linear expansion vertical (per m)',
  'LV-H': 'Joint linear horizontal (per m)',
  'DIL-H': 'Joint linear expansion horizontal (per m)',
  'CAN': 'Deck void',
  'BDV-m1': 'Penetration structural (per m)',
  'BDV-m2': 'Penetration structural (per m²)',
  'BDV-#': 'Penetration structural special',
  'GVA': 'Seal facade-floor junction',
  'Meld': 'Notification report-to-client',
  'Overleg': 'Request consultation',
  'ISO -/+': 'Insulation remove/replace',
  'BGL-R': 'Support Bracket round-penetration',
  'BGL-V': 'Support Bracket rectangular-penetration',
  'QR-sticker': 'Sticker QR extra',
  'Repair': 'Damage repair',
  'PUR -': 'Remove PU Foam',
  'SPAR +': 'mutate Opening enlarge',
  'Plaf -/+': 'Ceiling remove/replace',
  'Stofschot': 'Screen dust prtection',
  'Meubels': 'Furniture move',
  'DV -': 'Penetration remove',
  'Tijd bereik': 'Time access extra',
  'Tijd': 'Time extra general',
  'PH plaat': 'Plate Proma-H mounting',
  'Boor': 'Action drilling'
}

const SYSTEM_TRANSLATIONS = {
  'M701': 'FR seal acrylic',
  'M702': 'FR seal acrylic',
  'M703': 'FR seal silicone flexible',
  'M705': 'FR Seal graphite',
  'M709': 'FR Seal graphite',
  'M710': 'FR seal acrylic',
  'M721': 'FR seal acrylic grey',
  'M722': 'FR seal acrylic white',
  'WBD60 (1x)': 'FS Batt - mastic 1×60mm',
  'WBD60 (2x)': 'FS Batt - mastic 2×60mm',
  'WBD60': 'FS Batt - mastic',
  'WBD60 (1x) / Ductrock': 'FS Batt - mastic 1×60mm / Ductrock-insul',
  'WBD60 (1x) / Brandklep': 'FS Batt - mastic 1×60mm / fire-damper',
  'WBD60 (2x) / Ductrock': 'FS Batt - mastic 2×60mm / Ductrock-insul',
  'WBD60 (2x) / Brandklep': 'FS Batt - mastic 2×60mm / fire-damper',
  'WBD60 (1x) / Conlit U': 'FS Batt - mastic 1×60mm / Conlit-U',
  'WBD60 (1x) / Vlinderklep': 'FS Batt - mastic 1×60mm / butterfly-damper',
  'WBD60 (2x) / Conlit U': 'FS Batt - mastic 2×60mm / Conlit-U',
  'WBD60 (2x) / Vlinderklep': 'FS Batt - mastic 2×60mm / butterfly-damper',
  'B150 manchet / B747-60': 'FR Pipe Collar FR on Fire batt',
  'B150 manchet / M710': 'Collar intumescent B150 / acrylic-sealant',
  'B150 manchet': 'FR Pipe Collar',
  'B150': 'FR Pipe Collar',
  'B150 + WBD/PH': 'FR Pipe Collar',
  'B150 + beton': 'FR Pipe Collar',
  'B300': 'FR Pipe Wrap',
  'Putty Pads': 'Pads putty intumescent',
  'Schakelmanchet Promat': 'FR Pipe Collar',
  'PL strip': 'FS Strip intumescent',
  'FS700 + Steenwol 60kg': 'FR Seal on Fire batt',
  'Systeem J': 'FR Seal System-flex joint single',
  'Systeem J (dubbel)': 'FR Seal System-flex joint double',
  'Cannelurevulling (65 kg/m3)': 'Deck Voids filled 65kg/m³',
  'Cannelurevulling (150 kg/m3)': 'Deck Voids filled 150kg/m³',
  'Bicks B400': 'FR Bricks B400',
  'N.V.T.': 'N/A'
}

export default {
  components: {
    item: LogbookItemLabel,
    TitleBlock,
    TdUser
  },

  filters: {
    prettyDate: function (value) {
      if (!value) return ''
      const d = new Date(value)
      return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`
    },
    status: function (value) {
      if (!value) return 'no value'
      switch (value) {
        case 0: return 'None'
        case 1: return 'Quote: no QR'
        case 2: return 'Pre-inspection: not approved'
        case 3: return 'Pre-inspection: approved'
        case 4: return 'Assembly: not approved'
        case 5: return 'Assembly: approved'
        case 6: return 'Inspection: approved'
        case 7: return 'Inspection: not approved'
        case 8: return 'Periodic inspection: approved'
        case 9: return 'Mutation: repair'
        case 10: return 'Report: not approved'
        default: return 'no match: ' + value
      }
    }
  },

  data() {
    return {
      projects: [],
      selectedProject: null,
      buildings: [],
      selectedBuilding: null,
      floors: null,
      selectedFloor: null,
      zones: null,
      selectedZone: null,
      projectId: null,
      openings: [],
      isLoading: false,
      filterids: '',
      projectFilter: '',
      selectedTypeFilter: '',
      selectedSystemFilter: ''
    }
  },

  computed: {
    hasOpenings() {
      return this.openings != null && this.openings.length > 0
    },
    filteridarr() {
      return this.filterids.split(' ').map(item => Number(item))
    },
    filteredDataObj() {
      return this.projects.filter(option =>
        option.name.toString().toLowerCase().indexOf(this.projectFilter.toLowerCase()) >= 0
      )
    },
    availableTypes() {
      const types = new Set()
      this.openings.forEach(o => (o.penetrants || []).forEach(p => { if (p.type) types.add(p.type) }))
      return Array.from(types).sort()
    },
    availableSystems() {
      const systems = new Set()
      this.openings.forEach(o => (o.penetrants || []).forEach(p => { if (p.system) systems.add(p.system) }))
      return Array.from(systems).sort()
    },
    filteredOpenings() {
      let result = this.openings

      if (this.filterids !== '') {
        result = result.filter(o => this.filteridarr.includes(o.idOnDrawing))
      }

      if (this.selectedTypeFilter || this.selectedSystemFilter) {
        result = result.filter(o => this.filteredPenetrants(o).length > 0)
      }

      return result
    }
  },

  beforeCreate() {
    ProjectService.getProjectList().then(res => {
      this.projects = res.data.projects
    })
  },

  methods: {
    translateType(code) {
      return TYPE_TRANSLATIONS[code] || code
    },
    translateSystem(code) {
      return SYSTEM_TRANSLATIONS[code] || code
    },
    filteredPenetrants(opening) {
      let penetrants = opening.penetrants || []
      if (this.selectedTypeFilter) {
        penetrants = penetrants.filter(p => p.type === this.selectedTypeFilter)
      }
      if (this.selectedSystemFilter) {
        penetrants = penetrants.filter(p => p.system === this.selectedSystemFilter)
      }
      return penetrants
    },
    GetOrRefreshData() {
      this.selectedProject = null
      this.selectedBuilding = null
      this.selectedFloor = null
      this.selectedZone = null
      this.selectedTypeFilter = ''
      this.selectedSystemFilter = ''
    },
    displayName(value) {
      if (!value) return ''
      return `${value.projectId}: ${value.name}`
    },
    projectChangedHandler() {
      if (!this.selectedProject || !this.selectedProject.projectId) {
        this.selectedZone = null
        this.selectedProject = null
        this.buildings = null
        this.selectedBuilding = null
        this.selectedFloor = null
        this.floors = []
        this.zones = []
        return
      }
      this.projectId = this.selectedProject.projectId
      ProjectService.getProject(this.projectId).then(res => {
        this.selectedProject = res.data
        this.buildings = _.sortBy(res.data.buildings, 'name')
        this.selectedBuilding = null
        this.selectedFloor = null
        this.selectedZone = null
        this.floors = []
        this.zones = []
      })
    },
    buildingChangedHandler(value) {
      this.selectedBuilding = value
      this.floors = value.stories
      this.selectedFloor = null
      this.selectedZone = null
      this.zones = []
    },
    storyChangedHandler(value) {
      this.zones = value.zones
      this.selectedZone = null
      this.selectedFloor = value
    },
    zoneChangedHandler(value) {
      if (!this.selectedProject) return

      this.isLoading = true
      this.selectedZone = value
      this.selectedTypeFilter = ''
      this.selectedSystemFilter = ''

      let documentTitle
      if (this.selectedProject.name === this.selectedBuilding.name) {
        documentTitle = `${this.selectedProject.name} - Logbook ${this.selectedFloor.name} - ${this.selectedZone.name}`
      } else {
        documentTitle = `${this.selectedProject.name}, ${this.selectedBuilding.name} - Logbook ${this.selectedFloor.name} - ${this.selectedZone.name}`
      }
      document.title = documentTitle

      this.$store.dispatch('getBearerToken')

      OpeningService.getOpeningsForZone(this.projectId, value.zoneId).then(res => {
        this.openings = res.data.openings.map(o => this.mapLastMod(o))
        this.isLoading = false
      })
    },
    mapLastMod(opening) {
      opening.lastStatus = 0
      opening.lastComment = opening.comment
      opening.lastUser = opening.dbUser
      opening.lastDate = opening.dbDate

      if (opening.photos !== null && opening.photos.length > 0) {
        opening.lastphoto = opening.photos[0]
        opening.lastStatus = opening.photos[0].registrationType

        for (let index = 0; index < opening.photos.length; index++) {
          const element = opening.photos[index]
          if (element && element.captureTime > opening.lastphoto.captureTime) {
            opening.lastphoto = element
            opening.lastUser = element.captureUser
            opening.lastDate = element.captureTime
            opening.lastComment = element.comment
            opening.lastStatus = element.registrationType
          }
        }
      }
      return opening
    }
  }
}
</script>

<style lang="scss">
.filterbox {
  margin-bottom: 20px;
  h2 {
    font-weight: bold;
    margin-bottom: 5px;
  }
  textarea {
    width: 100%;
  }
}

@page {
  size: A4;
  margin: 0.4;
}

@media print {
  #Header,
  #Footer {
    display: none !important;
  }

  .content-block,
  p {
    page-break-inside: avoid;
  }

  html,
  body {
    width: 210mm;
    height: 297mm;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Office/LogbookInt.vue